import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-apollo';
import { defineMessages, useIntl } from 'react-intl-next';

import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/utility/cross';
import AutomationIcon from '@atlaskit/icon/core/automation';
import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { SPAViewContext } from '@confluence/spa-view-context';
import { useSpaceKey } from '@confluence/space-utils';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';

import { OnboardingManualTrialModalQuery } from '../PremiumTrialOnboarding/OnboardingManualTrialModalQuery.graphql';
import AnalyticsImageLight from '../images/analytics-image-light.svg';
import AdvancedPermissionImageLight from '../images/advanced-permission-image-light.svg';
import WhiteboardImageLight from '../images/whiteboard-image-light.svg';
import TeamCalendarsImageLight from '../images/team-calendars-image-light.svg';
import AnalyticsImageDark from '../images/analytics-image-dark.svg';
import AdvancedPermissionImageDark from '../images/advanced-permission-image-dark.svg';
import WhiteboardImageDark from '../images/whiteboard-image-dark.svg';
import TeamCalendarsImageDark from '../images/team-calendars-image-dark.svg';
import type { OnboardingManualTrialModalQuery as OnboardingManualTrialModalQueryType } from '../PremiumTrialOnboarding/__types__/OnboardingManualTrialModalQuery';
import { PremiumFeatureDeeplinks } from '../PremiumTrialOnboarding/PremiumFeatureDeeplinks';

import { PremiumFeature } from './PremiumFeatureTypes';
import type { PremiumFeatureItem } from './PremiumFeatureTypes';
import { PremiumFeatureTastingRightPanel } from './PremiumFeatureTastingRightPanel';
import { PremiumFeatureTastingGenericLeftPanel } from './PremiumFeatureTastingGenericLeftPanel';
import { PremiumFeatureTastingAutomationLeftPanel } from './PremiumFeatureTastingAutomationLeftPanel';

const i18n = defineMessages({
	automationTitle: {
		id: 'premium-trial.bite-sized.modal.automation.title',
		defaultMessage: 'Automations',
		description: 'Title for automation feature',
	},
	automationDescription: {
		id: 'premium-trial.bite-sized.modal.automation.description',
		defaultMessage:
			'Save time by creating rules to automate reminders, archiving inactive pages, and more.',
		description: 'Description for automation feature',
	},
	automationAction: {
		id: 'premium-trial.bite-sized.modal.automation.action',
		defaultMessage: 'Build an automation rule',
		description: 'Action for automation feature',
	},
	analyticsTitle: {
		id: 'premium-trial.bite-sized.modal.analytics.title',
		defaultMessage: 'Analytics',
		description: 'Title for analytics feature',
	},
	analyticsDescription: {
		id: 'premium-trial.bite-sized.modal.analytics.description',
		defaultMessage:
			'Get actionable insights on user engagement at the site, space, and page levels.',
		description: 'Description for analytics feature',
	},
	analyticsAction: {
		id: 'premium-trial.bite-sized.modal.analytics.action',
		defaultMessage: 'View analytics',
		description: 'Action for analytics feature',
	},
	teamCalendarsTitle: {
		id: 'premium-trial.bite-sized.modal.team-calendars.title',
		defaultMessage: 'Team calendars',
		description: 'Title for team calendars feature',
	},
	teamCalendarsDescription: {
		id: 'premium-trial.bite-sized.modal.team-calendars.description',
		defaultMessage:
			'Keep one single source of truth for your team’s schedules and view due dates directly from Jira.',
		description: 'Description for team calendars feature',
	},
	teamCalendarsAction: {
		id: 'premium-trial.bite-sized.modal.team-calendars.action',
		defaultMessage: 'Go to team calendars',
		description: 'Action for team calendars feature',
	},
	unlimitedWhiteboardsTitle: {
		id: 'premium-trial.bite-sized.modal.unlimited-whiteboards.title',
		defaultMessage: 'Unlimited whiteboards',
		description: 'Title for unlimited whiteboards feature',
	},
	unlimitedWhiteboardsDescription: {
		id: 'premium-trial.bite-sized.modal.unlimited-whiteboards.description',
		defaultMessage:
			'Brainstorm without boundaries and turn ideas into Jira tasks from one single location.',
		description: 'Description for unlimited whiteboards feature',
	},
	unlimitedWhiteboardsAction: {
		id: 'premium-trial.bite-sized.modal.unlimited-whiteboards.action',
		defaultMessage: 'Create a whiteboard',
		description: 'Action for unlimited whiteboards feature',
	},
	advancedPermissionsTitle: {
		id: 'premium-trial.bite-sized.modal.advanced-permissions.title',
		defaultMessage: 'Advanced permissions',
		description: 'Title for advanced permissions feature',
	},
	advancedPermissionsDescription: {
		id: 'premium-trial.bite-sized.modal.advanced-permissions.description',
		defaultMessage:
			'Review permission-related issues and make sure your team always has proper access to content.',
		description: 'Description for advanced permissions feature',
	},
	advancedPermissionsAction: {
		id: 'premium-trial.bite-sized.modal.advanced-permissions.action',
		defaultMessage: 'View space permissions',
		description: 'Action for advanced permissions feature',
	},
});

export type PremiumFeatureTastingModalProps = {
	showAutomation: boolean;
	trialDaysLeft: number;
	modalTrigger: MODAL_TRIGGER;
	onClose?: () => void;
};

export enum MODAL_TRIGGER {
	GENERIC_PREMIUM_TIP = 'genericPremiumTip',
	AUTOMATION_PREMIUM_TIP = 'automationPremiumTip',
}

const MODAL_NAME = 'premiumFeatureTastingModal';

const rightPanelStyles = xcss({
	background: token('color.background.accent.blue.subtlest'),
});

const leftInnerModalStyles = xcss({
	padding: 'space.600',
});

const rightInnerModalStyles = xcss({
	paddingBlock: 'space.800',
	paddingInlineStart: 'space.800',
});

const closeIconStyles = xcss({
	padding: 'space.100',
});

export const PremiumFeatureTastingModal: FC<PremiumFeatureTastingModalProps> = ({
	showAutomation: initializedShowAutomation,
	trialDaysLeft,
	modalTrigger,
	onClose,
}) => {
	const [showAutomation, setShowAutomation] = useState<boolean>(initializedShowAutomation);
	const [featureShowing, setFeatureShowing] = useState<PremiumFeature>(PremiumFeature.AUTOMATION);
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const [isOpen, setIsOpen] = useState<boolean>(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const modalRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(modalRef);
	const hasLinkEventFiredWithDwellTime = useRef<boolean>(false);

	//Get feature deeplinks
	const currentSpaceKey = useSpaceKey();
	const {
		data: { personalSpace, popularSpaces, starredSpace, recentSpaces } = {},
		loading,
		error,
	} = useQuery<OnboardingManualTrialModalQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		OnboardingManualTrialModalQuery,
	);
	const personalSpaceKey =
		(personalSpace?.confluence?.hasPersonalSpace && personalSpace?.confluence?.space?.alias) ||
		undefined;
	const popularSpace = popularSpaces?.nodes.find((node) => node.content?.space?.type !== 'system');
	const popularSpaceKey = popularSpace?.content?.space?.alias || undefined;
	const starredSpaceKey = starredSpace?.nodes?.[0]?.alias || undefined;
	const recentSpaceKey = recentSpaces?.[0]?.alias || undefined;

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: MODAL_NAME,
				attributes: {
					isSiteAdmin,
					modalTrigger,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, modalTrigger]);

	const premiumFeatureDeeplinks = useMemo(() => {
		return new PremiumFeatureDeeplinks({
			personalSpaceKey,
			popularSpaceKey,
			starredSpaceKey,
			recentSpaceKey,
			currentSpaceKey: currentSpaceKey || undefined,
		});
	}, [personalSpaceKey, popularSpaceKey, starredSpaceKey, recentSpaceKey, currentSpaceKey]);

	const closeModal = useCallback(
		(actionSubjectId: string = '') => {
			actionSubjectId &&
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId,
						source: MODAL_NAME,
						attributes: {
							dwellTime: getDwellTime(),
							modalTrigger,
						},
					},
				}).fire();
			onClose?.();
			setIsOpen(false);
		},
		[createAnalyticsEvent, getDwellTime, modalTrigger, onClose],
	);

	const onLinkClick = useCallback(
		(feature: PremiumFeature) => {
			let dwellTime = {};
			if (!hasLinkEventFiredWithDwellTime.current) {
				dwellTime = { dwellTime: getDwellTime() };
				hasLinkEventFiredWithDwellTime.current = true;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'premiumFeatureDeeplink',
					source: MODAL_NAME,
					attributes: {
						feature,
						isSiteAdmin,
						...dwellTime,
						modalTrigger,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isSiteAdmin, getDwellTime, modalTrigger],
	);

	const handleSecondaryCTA = useCallback(() => {
		setFeatureShowing(PremiumFeature.ANALYTICS);
		setShowAutomation(false);
	}, []);

	const automationLink = isSiteAdmin
		? premiumFeatureDeeplinks.getAutomationAdmin()
		: premiumFeatureDeeplinks.getAutomationEndUser();

	const featureDetailsMap: PremiumFeatureItem[] = [
		{
			feature: PremiumFeature.AUTOMATION,
			icon: AutomationIcon,
			title: formatMessage(i18n.automationTitle),
			description: formatMessage(i18n.automationDescription),
			action: formatMessage(i18n.automationAction),
			actionLink: automationLink,
		},
		{
			feature: PremiumFeature.ANALYTICS,
			icon: ChartBarIcon,
			title: formatMessage(i18n.analyticsTitle),
			description: formatMessage(i18n.analyticsDescription),
			action: formatMessage(i18n.analyticsAction),
			imageLight: AnalyticsImageLight,
			imageDark: AnalyticsImageDark,
			actionLink: premiumFeatureDeeplinks.getAnalytics(),
		},
		{
			feature: PremiumFeature.TEAM_CALENDARS,
			icon: CalendarIcon,
			title: formatMessage(i18n.teamCalendarsTitle),
			description: formatMessage(i18n.teamCalendarsDescription),
			action: formatMessage(i18n.teamCalendarsAction),
			imageLight: TeamCalendarsImageLight,
			imageDark: TeamCalendarsImageDark,
			actionLink: premiumFeatureDeeplinks.getTeamCalendars(),
		},
		{
			feature: PremiumFeature.WHITEBOARD,
			icon: WhiteboardIcon,
			title: formatMessage(i18n.unlimitedWhiteboardsTitle),
			description: formatMessage(i18n.unlimitedWhiteboardsDescription),
			action: formatMessage(i18n.unlimitedWhiteboardsAction),
			imageLight: WhiteboardImageLight,
			imageDark: WhiteboardImageDark,
			actionLink: premiumFeatureDeeplinks.getWhiteboard({ source: MODAL_NAME }),
		},
		{
			feature: PremiumFeature.ADVANCED_PERMISSIONS,
			icon: LockLockedIcon,
			title: formatMessage(i18n.advancedPermissionsTitle),
			description: formatMessage(i18n.advancedPermissionsDescription),
			action: formatMessage(i18n.advancedPermissionsAction),
			imageLight: AdvancedPermissionImageLight,
			imageDark: AdvancedPermissionImageDark,
			actionLink: premiumFeatureDeeplinks.getSpacePermission(),
		},
	];

	if (error && !isErrorMarkedAsHandled(error)) {
		markErrorAsHandled(error);
	}

	return (
		<TransparentErrorBoundary attribution={Attribution.EDITIONS}>
			{!loading && (
				<ModalTransition>
					{isOpen && (
						<Modal autoFocus={false} width="x-large" height={620} shouldScrollInViewport>
							<Inline ref={modalRef} grow="fill" spread="space-between" alignBlock="stretch">
								<Stack grow="fill" space="space.400">
									<Stack xcss={leftInnerModalStyles} space="space.400" grow="fill">
										{showAutomation && (
											<PremiumFeatureTastingAutomationLeftPanel
												onClickSecondaryCTA={handleSecondaryCTA}
												automationLink={automationLink}
											/>
										)}
										{!showAutomation && (
											<PremiumFeatureTastingGenericLeftPanel
												featureShowing={featureShowing}
												setFeatureShowing={setFeatureShowing}
												featureDetailsMap={featureDetailsMap}
												trialDaysLeft={trialDaysLeft}
												onFeatureLinkClick={onLinkClick}
											/>
										)}
									</Stack>
								</Stack>
								<Inline
									xcss={rightPanelStyles}
									grow="fill"
									alignBlock="stretch"
									spread="space-between"
								>
									<Inline xcss={rightInnerModalStyles} grow="fill" alignBlock="stretch">
										<PremiumFeatureTastingRightPanel
											featureShowing={featureShowing}
											featureDetailsMap={featureDetailsMap}
											isAutomationPanel={showAutomation}
										/>
									</Inline>
									<Stack alignInline="end" xcss={closeIconStyles}>
										<IconButton
											appearance="subtle"
											label="Close modal"
											icon={CrossIcon}
											onClick={() => closeModal('premiumFeatureTastingModalCloseButton')}
										/>
									</Stack>
								</Inline>
							</Inline>
						</Modal>
					)}
				</ModalTransition>
			)}
		</TransparentErrorBoundary>
	);
};
